import { dataContext, formatGitOrigin } from '@sdk';
import { findProjectForTask, pullForTask, cloneForTask } from '@sdk/entities/task-receive';
import type { GitOrigin } from '@sdk';
import { Button } from '@src/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@src/components/ui/dialog';
import { Input } from '@src/components/ui/input';
import { notifyGitOutcome } from '@src/lib/git-outcome';
import { useDockNavigation } from '@src/navigation/useDockNavigation';
import { DockPointer } from '@src/navigation/DockPointer';
import { AlertTriangle, CheckCircle2, FolderOpen, GitBranch, Loader2 } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Trans, useLingui } from '@lingui/react/macro';

type Step = 'checking' | 'found' | 'not_found' | 'pulling' | 'cloning' | 'success' | 'error';

interface KnownProject {
  name: string;
  path: string;
}

interface FindResult {
  found: boolean;
  local_path: string | null;
  git_origin: GitOrigin | null;
  known_projects: KnownProject[];
}

interface Props {
  open: boolean;
  taskId: string;
  taskTitle: string;
  senderName: string;
  gitOrigin?: GitOrigin | null;
  onClose: () => void;
}

export function IncomingTaskDialog({ open, taskId, taskTitle, senderName, gitOrigin, onClose }: Props) {
  const { navigation } = useDockNavigation();
  const { t } = useLingui();
  const [step, setStep] = useState<Step>('checking');
  const [findResult, setFindResult] = useState<FindResult | null>(null);
  const [localPath, setLocalPath] = useState('');
  const [cloneTarget, setCloneTarget] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const hasChecked = useRef(false);

  const computeNode = useMemo(() => dataContext.computeNode, []);

  // Run find-project on open
  useEffect(() => {
    if (!open || !taskId) return;
    if (hasChecked.current) return;
    hasChecked.current = true;

    setStep('checking');
    setFindResult(null);
    setErrorMsg('');

    findProjectForTask(taskId, { gitOrigin })
      .then((result) => {
        setFindResult(result);
        setLocalPath(result.local_path ?? '');
        setStep(result.found ? 'found' : 'not_found');
      })
      .catch((err: unknown) => {
        const msg = err instanceof Error ? err.message : t`Failed to look up project.`;
        setErrorMsg(msg);
        setStep('error');
      });
  }, [open, taskId]);

  // Reset when dialog closes
  const handleClose = useCallback(() => {
    hasChecked.current = false;
    setStep('checking');
    setFindResult(null);
    setCloneTarget('');
    setErrorMsg('');
    onClose();
  }, [onClose]);

  const handleConfirmPull = useCallback(async () => {
    setStep('pulling');
    try {
      const result = await pullForTask(taskId, localPath || undefined, { gitOrigin });
      if (result.kind === 'conflict') {
        // The same stuck tree the footer's Pull leaves: the same toast and
        // Resolve, aimed at the task's repo — not a dialog step of its own.
        notifyGitOutcome('pull', result, result.local_path ?? localPath);
        handleClose();
      } else if (result.success) {
        setStep('success');
        setTimeout(() => {
          navigation.openDock(DockPointer.fromUrl('tasks', taskId));
          handleClose();
        }, 800);
      } else {
        setErrorMsg(result.error ?? t`Pull failed.`);
        setStep('error');
      }
    } catch (err: unknown) {
      setErrorMsg(err instanceof Error ? err.message : 'Pull failed.');
      setStep('error');
    }
  }, [taskId, localPath, gitOrigin, navigation, handleClose]);

  const handleConfirmClone = useCallback(async () => {
    if (!cloneTarget) return;
    setStep('cloning');
    try {
      const result = await cloneForTask(taskId, cloneTarget, { gitOrigin });
      if (result.kind === 'conflict') {
        notifyGitOutcome(
          'pull',
          { kind: 'conflict', branch: result.branch ?? null, message: result.message ?? '' },
          result.cloned_path ?? cloneTarget,
        );
        handleClose();
      } else if (result.success) {
        setStep('success');
        setTimeout(() => {
          navigation.openDock(DockPointer.fromUrl('tasks', taskId));
          handleClose();
        }, 800);
      } else {
        setErrorMsg(result.error ?? t`Clone failed.`);
        setStep('error');
      }
    } catch (err: unknown) {
      setErrorMsg(err instanceof Error ? err.message : t`Clone failed.`);
      setStep('error');
    }
  }, [taskId, cloneTarget, gitOrigin, navigation, handleClose]);

  const handlePickFolder = useCallback(async () => {
    if (!computeNode) return;
    try {
      const selected = await computeNode.openPathDialog();
      if (selected) setCloneTarget(selected);
    } catch {
      // user cancelled or picker unavailable
    }
  }, [computeNode]);

  const resolvedOrigin = findResult?.git_origin || gitOrigin || null;
  const resolvedBranch = resolvedOrigin?.branch || '';
  const originLabel = resolvedOrigin ? formatGitOrigin(resolvedOrigin) : '';
  const knownProjects = findResult?.known_projects ?? [];

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) handleClose();
      }}
    >
      <DialogContent className="max-w-lg">
        {/* Checking */}
        {step === 'checking' && (
          <>
            <DialogHeader>
              <DialogTitle>
                <Trans>Looking up project…</Trans>
              </DialogTitle>
              <DialogDescription>
                <Trans>Checking if you have a local clone of the repository.</Trans>
              </DialogDescription>
            </DialogHeader>
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          </>
        )}

        {/* Found — confirm pull */}
        {step === 'found' && (
          <>
            <DialogHeader>
              <DialogTitle>
                <Trans>Pull and open task</Trans>
              </DialogTitle>
              <DialogDescription>
                <Trans>
                  <strong>{senderName}</strong> shared <em>{taskTitle}</em> with you.
                </Trans>
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-1 rounded-md border bg-muted/40 p-3 text-sm">
              {originLabel && (
                <div className="flex items-center gap-2 text-muted-foreground">
                  <span className="shrink-0">
                    <Trans>Repo:</Trans>
                  </span>
                  <code className="truncate text-foreground">{originLabel}</code>
                </div>
              )}
              {resolvedBranch && (
                <div className="flex items-center gap-2 text-muted-foreground">
                  <GitBranch className="h-3.5 w-3.5 shrink-0" />
                  <code className="text-foreground">{resolvedBranch}</code>
                </div>
              )}
              {localPath && (
                <div className="flex items-center gap-2 text-muted-foreground">
                  <FolderOpen className="h-3.5 w-3.5 shrink-0" />
                  <code className="truncate text-foreground">{localPath}</code>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={handleClose}>
                <Trans>Cancel</Trans>
              </Button>
              <Button onClick={() => void handleConfirmPull()}>
                <Trans>Pull &amp; Open</Trans>
              </Button>
            </DialogFooter>
          </>
        )}

        {/* Pulling */}
        {step === 'pulling' && (
          <>
            <DialogHeader>
              <DialogTitle>
                <Trans>Pulling…</Trans>
              </DialogTitle>
            </DialogHeader>
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          </>
        )}

        {/* Not found — offer clone or known projects */}
        {step === 'not_found' && (
          <>
            <DialogHeader>
              <DialogTitle>
                <Trans>Project not found locally</Trans>
              </DialogTitle>
              <DialogDescription>
                <Trans>
                  <strong>{senderName}</strong> shared <em>{taskTitle}</em> with you, but we couldn't find a local clone
                  of the repository. Clone it to see the task.
                </Trans>
              </DialogDescription>
            </DialogHeader>

            {knownProjects.length > 0 && (
              <div className="space-y-1">
                <p className="text-xs font-medium text-muted-foreground">
                  <Trans>Known projects (click to clone alongside):</Trans>
                </p>
                <div className="max-h-28 overflow-y-auto rounded-md border text-xs">
                  {knownProjects.map((p) => (
                    <button
                      key={p.path}
                      type="button"
                      className="w-full truncate px-3 py-1.5 text-start hover:bg-accent"
                      onClick={() => setCloneTarget(p.path.replace(/[/\\][^/\\]+$/, ''))}
                    >
                      <span className="font-medium">{p.name}</span>{' '}
                      <span className="text-muted-foreground">{p.path}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="flex gap-2">
              <Input
                placeholder={t`Parent folder to clone into…`}
                value={cloneTarget}
                onChange={(e) => setCloneTarget(e.target.value)}
                className="flex-1 text-sm"
              />
              {computeNode && (
                <Button variant="outline" size="icon" onClick={() => void handlePickFolder()} title={t`Browse…`}>
                  <FolderOpen className="h-4 w-4" />
                </Button>
              )}
            </div>

            <DialogFooter>
              <Button variant="ghost" onClick={handleClose}>
                <Trans>Cancel</Trans>
              </Button>
              <Button onClick={() => void handleConfirmClone()} disabled={!cloneTarget}>
                <Trans>Clone &amp; Open</Trans>
              </Button>
            </DialogFooter>
          </>
        )}

        {/* Cloning */}
        {step === 'cloning' && (
          <>
            <DialogHeader>
              <DialogTitle>
                <Trans>Cloning…</Trans>
              </DialogTitle>
            </DialogHeader>
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          </>
        )}

        {/* Success */}
        {step === 'success' && (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <CheckCircle2 className="h-5 w-5 text-emerald-500" />
                <Trans>Done!</Trans>
              </DialogTitle>
              <DialogDescription>
                <Trans>Opening task…</Trans>
              </DialogDescription>
            </DialogHeader>
          </>
        )}

        {/* Error */}
        {step === 'error' && (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-destructive" />
                <Trans>Something went wrong</Trans>
              </DialogTitle>
              <DialogDescription asChild>
                <div>
                  <pre className="mt-2 max-h-32 overflow-auto whitespace-pre-wrap rounded bg-muted px-3 py-2 text-xs text-foreground">
                    {errorMsg}
                  </pre>
                  <p className="mt-3 text-sm text-muted-foreground">
                    <Trans>Please fix the issue in your repository and try again.</Trans>
                  </p>
                </div>
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="ghost" onClick={handleClose}>
                <Trans>Close</Trans>
              </Button>
              <Button onClick={() => void handleConfirmPull()}>
                <Trans>Retry</Trans>
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
